import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {Box, Typography} from '@mui/material'

import {trackEvent} from '@hconnect/common/logging/Analytics'
import {useMix} from '@hconnect/tuc-processes/fields/mix/useMix'

import {Features} from '../../../../../../../Organisms/Features'
import {MixCustomizationAnnouncement} from '../../../../../orderIntake/components/shared/MixCustomizationAnnouncement'

import {MaterialList} from './MaterialList'
import {MaterialSearchBar} from './MaterialSearchBar'

/**
 * First step of the RMC order stepper.
 * Allows the user to select a specific RMC material from a list.
 */
export const MaterialStep: React.FC = () => {
  const {t} = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')

  // Use the specialized mix hook from orderProcessesPackage instead of useFormContext directly
  const {
    availableMixes,
    selectedMaterial,
    selectedContract,
    selectedContractItemPositionNumber,
    setMix
  } = useMix()

  if (!availableMixes || availableMixes.length === 0) {
    return (
      <Box
        sx={{
          p: 2,
          height: '300px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography color="error">
          {t('orderIntake.rmc.steps.material.noMaterialsAvailable')}
        </Typography>
      </Box>
    )
  }

  const handleMixSelect = (
    materialId: string,
    contractItemPositionNumber: string,
    contractNumber: string
  ) => {
    // Track material selection event
    const selectedMix = availableMixes.find(
      (mix) =>
        mix.materialId === materialId &&
        mix.contractItemPositionNumber === contractItemPositionNumber &&
        mix.contractNumber === contractNumber
    )

    trackEvent('rmcMaterialSelected', {
      materialId,
      contractItemPositionNumber,
      materialName: selectedMix?.materialDescription || 'Unknown',
      businessLine: 'RMC'
    })

    setMix(materialId, contractItemPositionNumber, contractNumber)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  // Filter materials based on search term
  const filteredMixes = availableMixes.filter(
    (mix) =>
      mix.materialDescription?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      mix.customerMaterialDescription?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <Box>
      {/* Mix customization announcement - dismissible and controlled by local storage */}
      <Features name="enableMaterialVariationBanner">
        <MixCustomizationAnnouncement testId="mix-customization-announcement" />
      </Features>

      {/* Search input */}
      <MaterialSearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
      {/* Material selection list */}
      <MaterialList
        materials={filteredMixes}
        selectedMaterialId={selectedMaterial?.materialId || undefined}
        selectedContractItemPositionNumber={selectedContractItemPositionNumber}
        selectedContractNumber={selectedContract?.contractNumber}
        onSelectMaterial={handleMixSelect}
      />
    </Box>
  )
}
